import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { VideoCollection } from "./VideoCollection";
import { 
  Upload, 
  FileText, 
  Image, 
  Video, 
  Download, 
  Share2, 
  Trash2, 
  FolderPlus, 
  Folder,
  Eye,
  Award,
  BookOpen,
  ExternalLink,
  Plus,
  Edit2,
  GraduationCap
} from "lucide-react";
import { LessonPlanBuilder } from "./LessonPlanBuilder";
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface Portfolio {
  id: string;
  title: string;
  description: string;
  is_public: boolean;
  share_token: string;
  created_at: string;
  updated_at: string;
}

interface PortfolioFolder {
  id: string;
  name: string;
  color: string;
  position: number;
  created_at: string;
}

interface PortfolioArtifact {
  id: string;
  title: string;
  description: string;
  file_url: string;
  file_type: string;
  file_size: number;
  folder_id?: string;
  artifact_order: number;
  is_public: boolean;
  created_at: string;
  metadata?: any;
}

interface PortfolioBadge {
  id: string;
  badge_id: string;
  is_public: boolean;
  created_at: string;
  badges?: {
    id: string;
    name: string;
    description: string;
  };
}

interface PortfolioCertificate {
  id: string;
  certificate_id: string;
  is_public: boolean;
  created_at: string;
}

const FOLDER_COLORS = [
  "#3B82F6", // Blue
  "#10B981", // Green
  "#F59E0B", // Amber
  "#EF4444", // Red
  "#8B5CF6", // Violet
  "#F97316", // Orange
  "#06B6D4", // Cyan
  "#84CC16", // Lime
];

function SortableFolder({ folder, artifacts, onDropArtifact, onOpenFolder }: { 
  folder: PortfolioFolder; 
  artifacts: PortfolioArtifact[];
  onDropArtifact: (artifactId: string, folderId: string) => void;
  onOpenFolder: (folder: PortfolioFolder) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: folder.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const folderArtifacts = artifacts.filter(a => a.folder_id === folder.id);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onOpenFolder(folder);
  };

  return (
      <div ref={setNodeRef} style={style} {...attributes}>
        <Card className="h-32 cursor-pointer hover:shadow-md transition-shadow relative" onClick={handleClick}>
          <CardContent className="p-4 h-full flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <Folder style={{ color: folder.color }} className="h-5 w-5" />
              <span className="font-medium text-sm truncate">{folder.name}</span>
            </div>
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <span className="text-xs">{folderArtifacts.length} items</span>
            </div>
          </CardContent>
          {/* Drag handle */}
          <div 
            {...listeners} 
            className="absolute top-2 right-2 opacity-50 hover:opacity-100 transition-opacity cursor-move"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-4 h-4 bg-muted-foreground rounded flex items-center justify-center">
              <div className="w-2 h-2 bg-background rounded-sm"></div>
            </div>
          </div>
        </Card>
      </div>
  );
}

function SortableArtifact({ artifact, onTogglePublic }: { 
  artifact: PortfolioArtifact; 
  onTogglePublic: (id: string, isPublic: boolean) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: artifact.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return <Image className="h-4 w-4" />;
    if (fileType.startsWith('video/')) return <Video className="h-4 w-4" />;
    return <FileText className="h-4 w-4" />;
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Card className="cursor-pointer hover:shadow-md transition-shadow">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 mb-2">
            {getFileIcon(artifact.file_type)}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{artifact.title}</p>
              <p className="text-xs text-muted-foreground truncate">{artifact.description}</p>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {artifact.is_public ? 'Public' : 'Private'}
            </span>
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground">Share:</span>
              <Switch
                checked={artifact.is_public}
                onCheckedChange={(checked) => onTogglePublic(artifact.id, checked)}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function MyPortfolio() {
  const { user } = useAuth();
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [folders, setFolders] = useState<PortfolioFolder[]>([]);
  const [artifacts, setArtifacts] = useState<PortfolioArtifact[]>([]);
  const [badges, setBadges] = useState<PortfolioBadge[]>([]);
  const [certificates, setCertificates] = useState<PortfolioCertificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [selectedColor, setSelectedColor] = useState(FOLDER_COLORS[0]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isAddItemOpen, setIsAddItemOpen] = useState(false);
  const [availableBadges, setAvailableBadges] = useState<any[]>([]);
  const [availableCertificates, setAvailableCertificates] = useState<any[]>([]);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadDescription, setUploadDescription] = useState("");
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [selectedItemsToShare, setSelectedItemsToShare] = useState<{
    artifacts: string[];
    badges: string[];
    certificates: string[];
  }>({ artifacts: [], badges: [], certificates: [] });
  const [openFolder, setOpenFolder] = useState<PortfolioFolder | null>(null);
  const [viewingLessonPlan, setViewingLessonPlan] = useState<PortfolioArtifact | null>(null);
  
  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    if (user?.id) {
      loadPortfolioData();
      loadAvailableItems();
    }
  }, [user?.id]);

  const loadPortfolioData = async () => {
    try {
      // Load or create portfolio
      let { data: portfolioData, error: portfolioError } = await supabase
        .from('portfolios')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (portfolioError && portfolioError.code === 'PGRST116') {
        // Create portfolio if it doesn't exist
        const { data: newPortfolio, error: createError } = await supabase
          .from('portfolios')
          .insert([{
            user_id: user.id,
            title: 'My Portfolio',
            description: 'My professional development portfolio',
            is_public: false
          }])
          .select()
          .single();

        if (createError) throw createError;
        portfolioData = newPortfolio;
      } else if (portfolioError) {
        throw portfolioError;
      }

      setPortfolio(portfolioData);

      // Load folders
      const { data: foldersData } = await supabase
        .from('portfolio_folders')
        .select('*')
        .eq('portfolio_id', portfolioData.id)
        .order('position');

      setFolders(foldersData || []);

      // Load artifacts
      const { data: artifactsData } = await supabase
        .from('portfolio_artifacts')
        .select('*')
        .eq('portfolio_id', portfolioData.id)
        .order('artifact_order');

      setArtifacts(artifactsData || []);

      // Load badges with badge details
      const { data: portfolioBadgesData } = await supabase
        .from('portfolio_badges')
        .select('*')
        .eq('portfolio_id', portfolioData.id);

      if (portfolioBadgesData && portfolioBadgesData.length > 0) {
        const badgeIds = portfolioBadgesData.map(pb => pb.badge_id);
        const { data: badgeDetails } = await supabase
          .from('badges')
          .select('id, name, description')
          .in('id', badgeIds);

        // Merge portfolio badge data with badge details
        const mergedBadges = portfolioBadgesData.map(pb => ({
          ...pb,
          badges: badgeDetails?.find(b => b.id === pb.badge_id)
        }));
        
        setBadges(mergedBadges);
      } else {
        setBadges([]);
      }

      // Load certificates - simplified for now since relationships may not be set up  
      const { data: certificatesData } = await supabase
        .from('portfolio_certificates')
        .select('*')
        .eq('portfolio_id', portfolioData.id);

      setCertificates(certificatesData || []);

    } catch (error) {
      console.error('Error loading portfolio:', error);
      toast.error('Failed to load portfolio');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableItems = async () => {
    try {
      // Load user's earned badges that aren't already in portfolio
      const { data: userBadges } = await supabase
        .from('user_badges')
        .select(`
          badge_id,
          badges (
            id,
            name,
            description,
            image_url
          )
        `)
        .eq('user_id', user.id);

      console.log('User badges:', userBadges); // Debug log

      const portfolioBadgeIds = badges.map(b => b.badge_id);
      const availableBadges = userBadges?.filter(ub => 
        !portfolioBadgeIds.includes(ub.badge_id)
      ) || [];

      setAvailableBadges(availableBadges);

      // Load user's certificates that aren't already in portfolio
      const { data: userCertificates } = await supabase
        .from('issued_certificates')
        .select(`
          id,
          certificate_number,
          issue_date,
          certificate_templates (
            name,
            description
          )
        `)
        .eq('user_id', user.id);

      console.log('User certificates:', userCertificates); // Debug log

      const portfolioCertIds = certificates.map(c => c.certificate_id);
      const availableCerts = userCertificates?.filter(uc => 
        !portfolioCertIds.includes(uc.id)
      ) || [];

      setAvailableCertificates(availableCerts);

    } catch (error) {
      console.error('Error loading available items:', error);
    }
  };

  const handleOpenFolder = (folder: PortfolioFolder) => {
    setOpenFolder(folder);
  };

  const handleCloseFolder = () => {
    setOpenFolder(null);
  };

  const handleArtifactClick = (artifact: PortfolioArtifact) => {
    if (artifact.file_type === 'lesson_plan') {
      setViewingLessonPlan(artifact);
    } else if (artifact.file_url) {
      window.open(artifact.file_url, '_blank');
    } else {
      toast.info("This artifact doesn't have a viewable file attached");
    }
  };

  const createFolder = async () => {
    if (!portfolio || !newFolderName.trim()) return;

    try {
      const { data, error } = await supabase
        .from('portfolio_folders')
        .insert([{
          portfolio_id: portfolio.id,
          name: newFolderName.trim(),
          color: selectedColor,
          position: folders.length
        }])
        .select()
        .single();

      if (error) throw error;

      setFolders([...folders, data]);
      setNewFolderName("");
      setIsCreateFolderOpen(false);
      toast.success("Folder created successfully");
    } catch (error) {
      console.error('Error creating folder:', error);
      toast.error('Failed to create folder');
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    // Handle artifact being dropped on folder
    const artifact = artifacts.find(a => a.id === active.id);
    const targetFolder = folders.find(f => f.id === over.id);

    if (artifact && targetFolder) {
      try {
        const { error } = await supabase
          .from('portfolio_artifacts')
          .update({ folder_id: targetFolder.id })
          .eq('id', artifact.id);

        if (error) throw error;

        setArtifacts(artifacts.map(a => 
          a.id === artifact.id ? { ...a, folder_id: targetFolder.id } : a
        ));

        toast.success(`Moved "${artifact.title}" to "${targetFolder.name}"`);
      } catch (error) {
        console.error('Error moving artifact:', error);
        toast.error('Failed to move artifact');
      }
      return;
    }

    // Handle folder reordering
    const activeFolder = folders.find(f => f.id === active.id);
    const overFolder = folders.find(f => f.id === over.id);

    if (activeFolder && overFolder && activeFolder.id !== overFolder.id) {
      const oldIndex = folders.findIndex(f => f.id === activeFolder.id);
      const newIndex = folders.findIndex(f => f.id === overFolder.id);
      
      const newFolders = [...folders];
      const [removed] = newFolders.splice(oldIndex, 1);
      newFolders.splice(newIndex, 0, removed);

      // Update positions
      const updatedFolders = newFolders.map((folder, index) => ({
        ...folder,
        position: index
      }));

      setFolders(updatedFolders);

      // Save new positions to database
      try {
        const updates = updatedFolders.map(folder => 
          supabase
            .from('portfolio_folders')
            .update({ position: folder.position })
            .eq('id', folder.id)
        );

        await Promise.all(updates);
        toast.success("Folder order updated");
      } catch (error) {
        console.error('Error updating folder positions:', error);
        toast.error('Failed to save folder order');
        // Revert on error
        await loadPortfolioData();
      }
    }
  };

  const uploadArtifact = async () => {
    if (!portfolio || !uploadFile || !uploadTitle.trim()) return;

    try {
      // Upload file to storage
      const fileExtension = uploadFile.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExtension}`;
      const filePath = `${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('portfolio-artifacts')
        .upload(filePath, uploadFile);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('portfolio-artifacts')
        .getPublicUrl(filePath);

      // Create artifact record
      const { error: insertError } = await supabase
        .from('portfolio_artifacts')
        .insert([{
          portfolio_id: portfolio.id,
          title: uploadTitle.trim(),
          description: uploadDescription.trim(),
          file_url: publicUrl,
          file_type: uploadFile.type,
          file_size: uploadFile.size,
          artifact_order: artifacts.length
        }]);

      if (insertError) throw insertError;

      // Reload data
      await loadPortfolioData();
      setUploadFile(null);
      setUploadTitle("");
      setUploadDescription("");
      setIsAddItemOpen(false);
      toast.success("Artifact uploaded successfully!");

    } catch (error) {
      console.error('Error uploading artifact:', error);
      toast.error('Failed to upload artifact');
    }
  };

  const addBadgeToPortfolio = async (badgeId: string) => {
    if (!portfolio) return;

    try {
      const { error } = await supabase
        .from('portfolio_badges')
        .insert([{
          portfolio_id: portfolio.id,
          badge_id: badgeId
        }]);

      if (error) throw error;

      await loadPortfolioData();
      await loadAvailableItems();
      toast.success("Badge added to portfolio!");

    } catch (error) {
      console.error('Error adding badge:', error);
      toast.error('Failed to add badge');
    }
  };

  const addCertificateToPortfolio = async (certificateId: string) => {
    if (!portfolio) return;

    try {
      const { error } = await supabase
        .from('portfolio_certificates')
        .insert([{
          portfolio_id: portfolio.id,
          certificate_id: certificateId
        }]);

      if (error) throw error;

      await loadPortfolioData();
      await loadAvailableItems();
      toast.success("Certificate added to portfolio!");

    } catch (error) {
      console.error('Error adding certificate:', error);
      toast.error('Failed to add certificate');
    }
  };

  const toggleItemPublic = async (itemType: 'artifact' | 'badge' | 'certificate', id: string, isPublic: boolean) => {
    try {
      const table = itemType === 'artifact' ? 'portfolio_artifacts' : 
                   itemType === 'badge' ? 'portfolio_badges' : 'portfolio_certificates';
      
      const { error } = await supabase
        .from(table)
        .update({ is_public: isPublic })
        .eq('id', id);

      if (error) throw error;

      // Update local state
      if (itemType === 'artifact') {
        setArtifacts(artifacts.map(a => a.id === id ? { ...a, is_public: isPublic } : a));
      } else if (itemType === 'badge') {
        setBadges(badges.map(b => b.id === id ? { ...b, is_public: isPublic } : b));
      } else {
        setCertificates(certificates.map(c => c.id === id ? { ...c, is_public: isPublic } : c));
      }

      toast.success(`Item ${isPublic ? 'shared' : 'hidden'} successfully`);
    } catch (error) {
      console.error('Error toggling visibility:', error);
      toast.error('Failed to update sharing settings');
    }
  };

  const generateShareLink = async () => {
    if (!portfolio) return;

    try {
      // First update the selected items' visibility
      const updatePromises = [];
      
      // Update artifacts
      if (selectedItemsToShare.artifacts.length > 0) {
        updatePromises.push(
          supabase
            .from('portfolio_artifacts')
            .update({ is_public: false })
            .eq('portfolio_id', portfolio.id)
        );
        
        updatePromises.push(
          supabase
            .from('portfolio_artifacts')
            .update({ is_public: true })
            .in('id', selectedItemsToShare.artifacts)
        );
      }

      // Update badges
      if (selectedItemsToShare.badges.length > 0) {
        updatePromises.push(
          supabase
            .from('portfolio_badges')
            .update({ is_public: false })
            .eq('portfolio_id', portfolio.id)
        );
        
        updatePromises.push(
          supabase
            .from('portfolio_badges')
            .update({ is_public: true })
            .in('id', selectedItemsToShare.badges)
        );
      }

      // Update certificates
      if (selectedItemsToShare.certificates.length > 0) {
        updatePromises.push(
          supabase
            .from('portfolio_certificates')
            .update({ is_public: false })
            .eq('portfolio_id', portfolio.id)
        );
        
        updatePromises.push(
          supabase
            .from('portfolio_certificates')
            .update({ is_public: true })
            .in('id', selectedItemsToShare.certificates)
        );
      }

      await Promise.all(updatePromises);

      // Generate share token if it doesn't exist
      let shareToken = portfolio.share_token;
      if (!shareToken) {
        const { data: tokenData, error: tokenError } = await supabase
          .rpc('generate_portfolio_share_token');
        
        if (tokenError) throw tokenError;
        shareToken = tokenData;
      }

      // Enable public sharing
      const { data, error } = await supabase
        .from('portfolios')
        .update({ 
          is_public: true,
          share_token: shareToken
        })
        .eq('id', portfolio.id)
        .select()
        .single();

      if (error) throw error;

      const shareUrl = `${window.location.origin}/portfolio/${data.share_token}`;
      await navigator.clipboard.writeText(shareUrl);
      
      setPortfolio(data);
      setIsShareDialogOpen(false);
      toast.success("Share link copied to clipboard!");

      // Open the shared portfolio in a new window
      window.open(shareUrl, '_blank', 'noopener,noreferrer');

      // Reload data to reflect changes
      await loadPortfolioData();

    } catch (error) {
      console.error('Error generating share link:', error);
      toast.error('Failed to generate share link');
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const unfiledArtifacts = artifacts.filter(a => !a.folder_id);

  return (
    <div className="container mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold">My Portfolio</h1>
            <p className="text-muted-foreground">Showcase your professional development achievements</p>
            <p className="text-sm text-muted-foreground mt-1">
              💡 <strong>To add badges/certificates:</strong> Click "Add Item" → Select from earned items
            </p>
          </div>
        <div className="flex gap-2">

          <Dialog open={isCreateFolderOpen} onOpenChange={setIsCreateFolderOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <FolderPlus className="h-4 w-4 mr-2" />
                New Folder
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Folder</DialogTitle>
                <DialogDescription>
                  Organize your portfolio items with colored folders
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="folder-name">Folder Name</Label>
                  <Input
                    id="folder-name"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    placeholder="Enter folder name"
                  />
                </div>
                <div>
                  <Label>Color</Label>
                  <div className="flex gap-2 mt-2">
                    {FOLDER_COLORS.map(color => (
                      <button
                        key={color}
                        className={`w-8 h-8 rounded-full border-2 ${
                          selectedColor === color ? 'border-foreground' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: color }}
                        onClick={() => setSelectedColor(color)}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={createFolder} disabled={!newFolderName.trim()}>
                  Create Folder
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          
          <Dialog open={isShareDialogOpen} onOpenChange={setIsShareDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Share2 className="h-4 w-4 mr-2" />
                Share Portfolio
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Share Your Portfolio</DialogTitle>
                <DialogDescription>
                  Select which items you want to include in your public portfolio
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6">
                {/* Artifacts Selection */}
                {artifacts.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Artifacts ({artifacts.length})</h3>
                    <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-3 bg-muted/20">
                      {artifacts.map(artifact => (
                        <div key={artifact.id} className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            id={`artifact-${artifact.id}`}
                            checked={selectedItemsToShare.artifacts.includes(artifact.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedItemsToShare(prev => ({
                                  ...prev,
                                  artifacts: [...prev.artifacts, artifact.id]
                                }));
                              } else {
                                setSelectedItemsToShare(prev => ({
                                  ...prev,
                                  artifacts: prev.artifacts.filter(id => id !== artifact.id)
                                }));
                              }
                            }}
                            className="rounded"
                          />
                          <label htmlFor={`artifact-${artifact.id}`} className="flex-1 text-sm cursor-pointer">
                            <span className="font-medium">{artifact.title}</span>
                            {artifact.description && (
                              <span className="text-muted-foreground"> - {artifact.description}</span>
                            )}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Badges Selection */}
                {badges.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Badges ({badges.length})</h3>
                    <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-3 bg-muted/20">
                      {badges.map(badge => (
                        <div key={badge.id} className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            id={`badge-${badge.id}`}
                            checked={selectedItemsToShare.badges.includes(badge.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedItemsToShare(prev => ({
                                  ...prev,
                                  badges: [...prev.badges, badge.id]
                                }));
                              } else {
                                setSelectedItemsToShare(prev => ({
                                  ...prev,
                                  badges: prev.badges.filter(id => id !== badge.id)
                                }));
                              }
                            }}
                            className="rounded"
                          />
                          <label htmlFor={`badge-${badge.id}`} className="flex-1 text-sm cursor-pointer">
                            <span className="font-medium">{badge.badges?.name || 'Badge'}</span>
                            {badge.badges?.description && (
                              <span className="text-muted-foreground text-xs block">{badge.badges.description}</span>
                            )}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Certificates Selection */}
                {certificates.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Certificates ({certificates.length})</h3>
                    <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-3 bg-muted/20">
                      {certificates.map(cert => (
                        <div key={cert.id} className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            id={`cert-${cert.id}`}
                            checked={selectedItemsToShare.certificates.includes(cert.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedItemsToShare(prev => ({
                                  ...prev,
                                  certificates: [...prev.certificates, cert.id]
                                }));
                              } else {
                                setSelectedItemsToShare(prev => ({
                                  ...prev,
                                  certificates: prev.certificates.filter(id => id !== cert.id)
                                }));
                              }
                            }}
                            className="rounded"
                          />
                          <label htmlFor={`cert-${cert.id}`} className="flex-1 text-sm cursor-pointer">
                            <span className="font-medium">Certificate #{cert.certificate_id}</span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {artifacts.length === 0 && badges.length === 0 && certificates.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No items to share yet. Add some artifacts, badges, or certificates first!</p>
                  </div>
                )}
              </div>

              <DialogFooter>
                <div className="flex items-center justify-between w-full">
                  <p className="text-sm text-muted-foreground">
                    {selectedItemsToShare.artifacts.length + selectedItemsToShare.badges.length + selectedItemsToShare.certificates.length} items selected
                  </p>
                  <Button 
                    onClick={generateShareLink}
                    disabled={selectedItemsToShare.artifacts.length + selectedItemsToShare.badges.length + selectedItemsToShare.certificates.length === 0}
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Generate Share Link
                  </Button>
                </div>
              </DialogFooter>
            </DialogContent>
          </Dialog>


          <Button size="sm" onClick={() => setIsAddItemOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Item
          </Button>
        </div>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="portfolio" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="portfolio" className="flex items-center gap-2">
            <Folder className="h-4 w-4" />
            Portfolio Collection
          </TabsTrigger>
          <TabsTrigger value="lesson-plans" className="flex items-center gap-2">
            <GraduationCap className="h-4 w-4" />
            AI Lesson Planner
          </TabsTrigger>
        </TabsList>

        <TabsContent value="lesson-plans" className="space-y-6">
          <LessonPlanBuilder />
        </TabsContent>

        <TabsContent value="portfolio" className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Artifacts</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{artifacts.length}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Badges</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{badges.length}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Certificates</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{certificates.length}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Folders</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{folders.length}</div>
              </CardContent>
            </Card>
          </div>

      {/* Add Item Dialog */}
      <Dialog open={isAddItemOpen} onOpenChange={setIsAddItemOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add to Portfolio</DialogTitle>
            <DialogDescription>
              Upload artifacts or add earned badges and certificates to your portfolio
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Upload Artifact Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Upload Artifact</h3>
              <div className="grid gap-4">
                <div>
                  <Label htmlFor="file-upload">File</Label>
                  <Input 
                    id="file-upload"
                    type="file" 
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    accept=".pdf,.doc,.docx,.ppt,.pptx,.mp4,.avi,.jpg,.jpeg,.png,.gif"
                  />
                </div>
                <div>
                  <Label htmlFor="artifact-title">Title</Label>
                  <Input
                    id="artifact-title"
                    value={uploadTitle}
                    onChange={(e) => setUploadTitle(e.target.value)}
                    placeholder="Enter artifact title"
                  />
                </div>
                <div>
                  <Label htmlFor="artifact-description">Description</Label>
                  <Textarea
                    id="artifact-description"
                    value={uploadDescription}
                    onChange={(e) => setUploadDescription(e.target.value)}
                    placeholder="Describe your artifact"
                    rows={3}
                  />
                </div>
                <Button 
                  onClick={uploadArtifact}
                  disabled={!uploadFile || !uploadTitle.trim()}
                  className="w-full"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Artifact
                </Button>
              </div>
            </div>

            {/* Available Badges Section */}
            {availableBadges.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Add Earned Badges ({availableBadges.length} available)</h3>
                <div className="grid grid-cols-2 gap-3 max-h-40 overflow-y-auto border rounded-lg p-3 bg-muted/20">
                  {availableBadges.map(userBadge => (
                    <Card key={userBadge.badge_id} className="cursor-pointer hover:shadow-md transition-shadow">
                      <CardContent className="p-3">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                            <Award className="h-5 w-5 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{userBadge.badges?.name || 'Badge'}</p>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => addBadgeToPortfolio(userBadge.badge_id)}
                              className="h-6 px-2 text-xs"
                            >
                              Add
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Available Certificates Section */}
            {availableCertificates.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Add Earned Certificates ({availableCertificates.length} available)</h3>
                <div className="grid grid-cols-1 gap-3 max-h-40 overflow-y-auto border rounded-lg p-3 bg-muted/20">
                  {availableCertificates.map(cert => (
                    <Card key={cert.id} className="cursor-pointer hover:shadow-md transition-shadow">
                      <CardContent className="p-3">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center">
                            <BookOpen className="h-5 w-5 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {cert.certificate_templates?.name || 'Certificate'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              #{cert.certificate_number}
                            </p>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => addCertificateToPortfolio(cert.id)}
                              className="h-6 px-2 text-xs mt-1"
                            >
                              Add
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {availableBadges.length === 0 && availableCertificates.length === 0 && (
              <div className="text-center py-4 text-muted-foreground border rounded-lg bg-muted/20">
                <p className="font-medium">No additional badges or certificates available</p>
                <p className="text-sm mt-1">Complete courses and earn achievements to add more items!</p>
                <p className="text-sm mt-2">📚 Visit the Course Catalog to start learning</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        {/* Folders */}
        {folders.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Folders</h2>
            <SortableContext items={folders.map(f => f.id)}>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {folders.map(folder => (
                  <SortableFolder
                    key={folder.id}
                    folder={folder}
                    artifacts={artifacts}
                    onDropArtifact={() => {}}
                    onOpenFolder={handleOpenFolder}
                  />
                ))}
              </div>
            </SortableContext>
          </div>
        )}

        {/* Unfiled Artifacts */}
        {unfiledArtifacts.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Artifacts</h2>
            <SortableContext items={unfiledArtifacts.map(a => a.id)}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {unfiledArtifacts.map(artifact => (
                  <SortableArtifact 
                    key={artifact.id} 
                    artifact={artifact} 
                    onTogglePublic={(id, isPublic) => toggleItemPublic('artifact', id, isPublic)}
                  />
                ))}
              </div>
            </SortableContext>
          </div>
        )}

        <DragOverlay>
          {activeId ? (() => {
            const draggedFolder = folders.find(f => f.id === activeId);
            const draggedArtifact = artifacts.find(a => a.id === activeId);
            
            if (draggedFolder) {
              return (
                <Card className="h-32 shadow-2xl rotate-3 opacity-90">
                  <CardContent className="p-4 h-full flex flex-col">
                    <div className="flex items-center gap-2 mb-2">
                      <Folder style={{ color: draggedFolder.color }} className="h-5 w-5" />
                      <span className="font-medium text-sm truncate">{draggedFolder.name}</span>
                    </div>
                    <div className="flex-1 flex items-center justify-center text-muted-foreground">
                      <span className="text-xs">
                        {artifacts.filter(a => a.folder_id === draggedFolder.id).length} items
                      </span>
                    </div>
                  </CardContent>
                </Card>
              );
            }
            
            if (draggedArtifact) {
              return (
                <Card className="shadow-2xl rotate-3 opacity-90">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      {draggedArtifact.file_type.startsWith('image/') ? (
                        <Image className="h-4 w-4 text-primary" />
                      ) : draggedArtifact.file_type.startsWith('video/') ? (
                        <Video className="h-4 w-4 text-primary" />
                      ) : (
                        <FileText className="h-4 w-4 text-primary" />
                      )}
                      <span className="text-sm font-medium truncate">{draggedArtifact.title}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            }
            
            return (
              <div className="bg-background border rounded-lg p-3 shadow-lg">
                Dragging...
              </div>
            );
          })() : null}
        </DragOverlay>
      </DndContext>

      {/* Badges */}
      {badges.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold mb-4">Badges</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {badges.map(badge => (
              <Card key={badge.id} className="text-center">
                <CardContent className="p-4">
                  <div className="h-16 w-16 mx-auto mb-2 bg-primary/10 rounded-full flex items-center justify-center">
                    <Award className="h-8 w-8 text-primary" />
                  </div>
                  <p className="text-sm font-medium mb-2">{badge.badges?.name || 'Badge'}</p>
                  <div className="flex items-center justify-center gap-1">
                    <span className="text-xs text-muted-foreground">Share:</span>
                    <Switch
                      checked={badge.is_public}
                      onCheckedChange={(checked) => toggleItemPublic('badge', badge.id, checked)}
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Certificates */}
      {certificates.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold mb-4">Certificates</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {certificates.map(cert => (
              <Card key={cert.id}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-12 w-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <BookOpen className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">Certificate #{cert.certificate_id}</p>
                      <p className="text-xs text-muted-foreground">
                        Added: {new Date(cert.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      {cert.is_public ? 'Public' : 'Private'}
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-muted-foreground">Share:</span>
                      <Switch
                        checked={cert.is_public}
                        onCheckedChange={(checked) => toggleItemPublic('certificate', cert.id, checked)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

          {/* Empty State */}
          {artifacts.length === 0 && badges.length === 0 && certificates.length === 0 && (
            <Card className="text-center py-12">
              <CardContent>
                <Upload className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">Start Building Your Portfolio</h3>
                <p className="text-muted-foreground mb-4">
                  Upload learning artifacts, earn badges, and showcase your professional development journey.
                </p>
                <Button onClick={() => setIsAddItemOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Item
                </Button>
              </CardContent>
            </Card>
          )}
          {/* Add Videos section to MyPortfolio */}
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">Video Collections</h2>
            <VideoCollection portfolioId={portfolio?.id} isOwner={true} />
          </div>
        </TabsContent>
      </Tabs>

      {/* Folder View Dialog */}
      <Dialog open={!!openFolder} onOpenChange={() => handleCloseFolder()}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Folder style={{ color: openFolder?.color }} className="h-5 w-5" />
              {openFolder?.name}
            </DialogTitle>
            <DialogDescription>
              View and manage items in this folder
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {openFolder && (() => {
              const folderArtifacts = artifacts.filter(a => a.folder_id === openFolder.id);
              
              if (folderArtifacts.length === 0) {
                return (
                  <div className="text-center py-8 text-muted-foreground">
                    <Folder className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">This folder is empty</p>
                    <p className="text-sm">Drag and drop artifacts here to organize them</p>
                  </div>
                );
              }
              
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {folderArtifacts.map(artifact => (
                    <Card 
                      key={artifact.id} 
                      className="hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => handleArtifactClick(artifact)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center">
                            {artifact.file_type.startsWith('image/') ? (
                              <Image className="h-5 w-5 text-primary" />
                            ) : artifact.file_type.startsWith('video/') ? (
                              <Video className="h-5 w-5 text-primary" />
                            ) : artifact.file_type === 'lesson_plan' ? (
                              <GraduationCap className="h-5 w-5 text-primary" />
                            ) : (
                              <FileText className="h-5 w-5 text-primary" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{artifact.title}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {artifact.description}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">
                            {artifact.is_public ? 'Public' : 'Private'}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">Click to view</span>
                            <div 
                              className="flex items-center gap-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <span className="text-xs text-muted-foreground">Share:</span>
                              <Switch
                                checked={artifact.is_public}
                                onCheckedChange={(checked) => {
                                  toggleItemPublic('artifact', artifact.id, checked);
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Lesson Plan Viewer Dialog */}
      <Dialog open={!!viewingLessonPlan} onOpenChange={() => setViewingLessonPlan(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-primary" />
              {viewingLessonPlan?.title}
            </DialogTitle>
            <DialogDescription>
              {viewingLessonPlan?.description}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {viewingLessonPlan && viewingLessonPlan.metadata?.lesson_plan_data && (() => {
              const lessonData = viewingLessonPlan.metadata.lesson_plan_data;
              
              return (
                <>
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/20 rounded-lg">
                    <div>
                      <Label className="text-xs font-medium text-muted-foreground">Subject</Label>
                      <p className="text-sm font-medium">{lessonData.subject}</p>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-muted-foreground">Grade Level</Label>
                      <p className="text-sm font-medium">{lessonData.grade_level}</p>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-muted-foreground">Duration</Label>
                      <p className="text-sm font-medium">{lessonData.duration} minutes</p>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-muted-foreground">Created</Label>
                      <p className="text-sm font-medium">
                        {new Date(lessonData.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  {/* Learning Objectives */}
                  {lessonData.objectives && lessonData.objectives.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Learning Objectives</h3>
                      <ul className="space-y-2">
                        {lessonData.objectives.map((objective: string, index: number) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-primary font-semibold">•</span>
                            <span className="text-sm">{objective}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Standards */}
                  {lessonData.standards && lessonData.standards.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Standards</h3>
                      <div className="space-y-2">
                        {lessonData.standards.map((standard: string, index: number) => (
                          <div key={index} className="text-sm p-3 bg-muted/10 rounded border-l-4 border-primary">
                            {standard}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Activities */}
                  {lessonData.activities && lessonData.activities.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Activities</h3>
                      <div className="space-y-4">
                        {lessonData.activities.map((activity: any, index: number) => (
                          <Card key={index} className="p-4">
                            <div className="flex items-start gap-3">
                              <div className="h-8 w-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-xs font-semibold text-primary">{index + 1}</span>
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h4 className="font-semibold">{activity.name}</h4>
                                  <span className="text-xs px-2 py-1 bg-muted rounded-full">
                                    {activity.duration} min
                                  </span>
                                  <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full">
                                    {activity.grouping}
                                  </span>
                                </div>
                                <p className="text-sm text-muted-foreground mb-2">{activity.description}</p>
                                {activity.growth_mindset_connection && (
                                  <div className="text-xs p-2 bg-green-50 border border-green-200 rounded">
                                    <strong>Growth Mindset:</strong> {activity.growth_mindset_connection}
                                  </div>
                                )}
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Materials */}
                  {lessonData.materials && lessonData.materials.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Materials Needed</h3>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {lessonData.materials.map((material: string, index: number) => (
                          <li key={index} className="flex items-center gap-2 text-sm">
                            <span className="h-2 w-2 bg-primary rounded-full"></span>
                            {material}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Assessment */}
                  {lessonData.assessment && (
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Assessment</h3>
                      <div className="text-sm p-4 bg-muted/10 rounded-lg">
                        {lessonData.assessment}
                      </div>
                    </div>
                  )}

                  {/* Differentiation */}
                  {lessonData.differentiation && (
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Differentiation</h3>
                      <div className="text-sm p-4 bg-muted/10 rounded-lg">
                        {lessonData.differentiation}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}